import React, { useEffect, useState } from "react";
import { Card, Row, Col, Spin, Empty, Alert, Divider, Button, Drawer, Timeline } from "antd";
import { useDispatch } from "react-redux";
import { getPricingConfigs, fetchPricingModel } from "../../store/slice/pricing";
import PricingSettingCard from "./PricingSettingCard";
import PricingModelSelector from "./PricingModelSelector";
import BaseFeesPanel from "./BaseFeesPanel";
import DirectValuesMatrix from "./DirectValuesMatrix";
import OverrideManager from "./OverrideManager";
import { usePricingManager } from "./usePricingManager";
import { LOCALITY_TYPES, PRICING_MODELS, PRICING_FORMULA } from "./constants";
import "../../styles/pricing-management.css";
import { DownloadOutlined, HistoryOutlined } from "@ant-design/icons";

function PricingSettings() {
  const dispatch = useDispatch();
  const [showAuditLog, setShowAuditLog] = useState(false);
  const {
    configs,
    isLoading,
    currentPricingModel,
    lastChanges,
    handleSave,
    handleExportConfig,
    getConfigByLocality,
  } = usePricingManager();

  useEffect(() => {
    dispatch(getPricingConfigs());
    dispatch(fetchPricingModel());
  }, [dispatch]);

  if (isLoading) {
    return (
      <Card title="PRICING SETTINGS" className="flex items-center justify-center py-12">
        <Spin size="large" />
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <PricingHeader
        auditLogCount={lastChanges.length}
        onShowAuditLog={() => setShowAuditLog(true)}
        onExport={handleExportConfig}
      />

      <PricingModelSelector />

      {currentPricingModel === PRICING_MODELS.BASE_MULTIPLIER && (
        <div className="space-y-6">
          <BaseFeesPanel />
          <LegacyLocalityConfiguration
            localities={LOCALITY_TYPES}
            configs={configs}
            onSave={handleSave}
            getConfigByLocality={getConfigByLocality}
          />
          <PricingExamples />
        </div>
      )}

      {currentPricingModel === PRICING_MODELS.DIRECT_VALUES && (
        <div className="space-y-6">
          <DirectValuesMatrix />
          <PricingExamples />
        </div>
      )}

      {currentPricingModel === PRICING_MODELS.HYBRID && (
        <div className="space-y-6">
          <BaseFeesPanel />
          <OverrideManager />
          <PricingExamples />
        </div>
      )}

      <AuditLogDrawer
        open={showAuditLog}
        onClose={() => setShowAuditLog(false)}
        changes={lastChanges}
      />
    </div>
  );
}

function PricingHeader({ auditLogCount, onShowAuditLog, onExport }) {
  return (
    <Card>
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold mb-2">Pricing Management</h2>
          <p className="text-gray-600">
            Manage flexible pricing models and configure pricing by locality and demand tier.
          </p>
        </div>
        <div className="flex gap-2">
          <Button icon={<HistoryOutlined />} onClick={onShowAuditLog}>
            Audit Log ({auditLogCount})
          </Button>
          <Button
            icon={<DownloadOutlined />}
            onClick={onExport}
            className="bg-orange-500 hover:bg-orange-600 text-white"
          >
            Export
          </Button>
        </div>
      </div>
    </Card>
  );
}

function LegacyLocalityConfiguration({
  localities,
  configs,
  onSave,
  getConfigByLocality,
}) {
  return (
    <>
      <Divider titlePlacement="left" plain>
        <span className="text-sm font-medium text-gray-600">
          Legacy Configuration by Locality
        </span>
      </Divider>

      <Card
        title="Configure by Locality"
        extra={
          <span className="text-xs text-gray-600">
            (Alternative interface - same data as Base Fees Panel above)
          </span>
        }
        className="opacity-75"
      >
        <Alert
          type="info"
          showIcon
          message="Base Pricing Formula"
          description={PRICING_FORMULA}
          className="mb-6"
        />

        <Row gutter={[24, 24]}>
          {localities.map((locality) => (
            <Col xs={24} sm={24} md={12} lg={12} key={locality.key}>
              <PricingSettingCard
                locality={locality}
                config={getConfigByLocality(locality.key)}
                onSave={onSave}
              />
            </Col>
          ))}
        </Row>
      </Card>
    </>
  );
}

function AuditLogDrawer({ open, onClose, changes }) {
  return (
    <Drawer
      title="Audit Log"
      placement="right"
      onClose={onClose}
      open={open}
      width={500}
    >
      {changes.length === 0 ? (
        <Empty description="No changes recorded" />
      ) : (
        <Timeline
          items={changes.map((change) => ({
            label: new Date(change.timestamp).toLocaleString("en-IN"),
            children: (
              <div>
                <p className="font-semibold text-sm mb-1">{change.action}</p>
                <pre className="bg-gray-100 p-2 rounded text-xs overflow-auto max-h-32">
                  {JSON.stringify(change.details, null, 2)}
                </pre>
              </div>
            ),
          }))}
        />
      )}
    </Drawer>
  );
}

function PricingExamples() {
  return (
    <Card
      title="Pricing Examples"
      extra={
        <span className="text-xs text-gray-600">
          How pricing works with different demand scenarios
        </span>
      }
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <ExampleScenario
          title="Scenario 1: Electrician (High Demand) - Joining Fee"
          backgroundColor="bg-blue-50"
          borderColor="border-blue-200"
          textColor="text-blue-900"
          examples={[
            { location: "Metro", base: 199, result: 299 },
            { location: "Urban", base: 149, result: 224 },
            { location: "Suburban", base: 99, result: 149 },
            { location: "Rural", base: 49, result: 74 },
          ]}
        />

        <ExampleScenario
          title="Scenario 2: Carpenter (Low Demand) - Pro Monthly"
          backgroundColor="bg-green-50"
          borderColor="border-green-200"
          textColor="text-green-900"
          examples={[
            { location: "Metro", base: 999, result: 599 },
            { location: "Urban", base: 749, result: 449 },
            { location: "Suburban", base: 499, result: 299 },
            { location: "Rural", base: 299, result: 179 },
          ]}
        />
      </div>
    </Card>
  );
}

function ExampleScenario({
  title,
  backgroundColor,
  borderColor,
  textColor,
  examples,
}) {
  return (
    <div className={`${backgroundColor} p-4 rounded border ${borderColor}`}>
      <h4 className={`font-semibold ${textColor} mb-3`}>{title}</h4>
      <div className="space-y-2 text-sm">
        {examples.map((example, index) => (
          <p key={index} className="text-gray-700">
            <span className="font-medium">{example.location}:</span> ₹{example.base} × 1.5 = ₹
            {example.result}
          </p>
        ))}
      </div>
    </div>
  );
}

export default PricingSettings;
